import React, { useEffect, useState, useMemo } from "react";
import { TextHeader } from "components/Texts";
import { useDispatch, useSelector } from "react-redux";
import _forEach from "lodash/forEach";
import Input from "components/Inputs";
import Button from "components/Buttons";

import Row from "components/Row";
import Toast from "components/Toast";
import SigninButton from "components/gads/SigninButton";
import Loading from "components/gads/Loading";
import styled from "styled-components";
import useWindowSize from "../../layout/useWindowSize";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import { useTranslation } from "react-i18next";

const Setup = () => {
  const [t] = useTranslation();
  const [adsId, setAdsId] = useState("");
  const rmpToken = sessionStorage.getItem("rmp_token");
  const [loading, setLoading] = useState(false);
  const [adsKeyword, setAdsKeyword] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);
  const [showValidateFailedToast, setShowValidateFailedToast] = useState(false);
  const dispatch = useDispatch();
  const userInfoResult = useSelector((state) => googleSelectors.getUserInfoResult(state));
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const accountHierarchyResult = useSelector((state) => googleSelectors.getAccountHierarchyResult(state));

  //eslint-disable-next-line no-unused-vars
  const windowSize = useWindowSize();

  useEffect(() => {
    setPageLoading(true);

    if (userInfoResult.data.userInfo.isManual === true) {
      setAdsId("");
      setPageLoading(false);
    } else {
      dispatch(
        googleActions.getAccountHierarchy(rmpToken, {
          business_id: businessInfoResult.data.business_id,
          agency_id: businessInfoResult.data.agency_id,
          adwords_id: businessInfoResult.data.adwords_id,
          adwords_code: businessInfoResult.data.adwords_code,
          refresh_token: userInfoResult.data.userInfo.access_token,
          adwords_email: userInfoResult.data.userInfo.email,
        }),
      );
    }
  }, [userInfoResult]);

  useEffect(() => {
    if (accountHierarchyResult.result === 0) {
      setAdsId("");
      setPageLoading(false);
    } else if (accountHierarchyResult.result > 0 && userInfoResult.data.userInfo.isManual !== true) {
      accountHierarchyResult.result = "";
      dispatch(googleActions.showNoAdsToast(true));
      dispatch(googleActions.changeSettingPage("signin"));
    }
  }, [accountHierarchyResult]);

  const handleSaveButton = async () => {
    try {
      if (pageLoading === false) {
        if (!adsId || (adsId && String(adsId).trim() === "")) {
          setShowValidateFailedToast(true);

          return false;
        }

        setLoading(true);
        const lang = sessionStorage.getItem("lang") ? sessionStorage.getItem("lang") : "en";

        const autoLinkAccount = await dispatch(
          googleActions.autoLinkAccount({
            token: rmpToken,
            business_id: businessInfoResult.data.business_id,
            agency_id: businessInfoResult.data.agency_id,
            adwords_id: adsId,
            refresh_token: userInfoResult.data.userInfo.access_token,
          }),
        );

        if (autoLinkAccount.result !== 0) {
          throw new Error("LINK_ADS_ACCOUNT_FAILED");
        }

        await dispatch(
          googleActions.setBusinessAdwordsId({
            token: rmpToken,
            business_id: businessInfoResult.data.business_id,
            adwords_id: adsId,
            adwords_managed_service: "N",
          }),
        );

        await dispatch(
          googleActions.setBusinessCanAccess({
            token: rmpToken,
            can_access_adwords: "Y",
          }),
        );

        await dispatch(
          googleActions.setBusinessAdwordsName({
            token: rmpToken,
            business_id: businessInfoResult.data.business_id,
            adwords_name: autoLinkAccount.data.account_name,
          }),
        );

        const getAdwordsConversionsData = await dispatch(
          googleActions.getAdwordsConversionsData({
            token: rmpToken,
            business_plan: businessInfoResult.data.business_plan_category,
          }),
        );

        // Loop create Offline Conversion from getAdwordsConversionsData
        for (let i = 0; i < getAdwordsConversionsData.data.length; i += 1) {
          if (
            getAdwordsConversionsData.data[i].feature_name &&
            (getAdwordsConversionsData.data[i].lead_status_id ||
              getAdwordsConversionsData.data[i].lead_status_id == "0") &&
            getAdwordsConversionsData.data[i].conversion_name
          ) {
            dispatch(
              googleActions.createOfflineConversion({
                token: rmpToken,
                business_id: businessInfoResult.data.business_id,
                adwords_id: adsId,
                agency_id: businessInfoResult.data.agency_id,
                lead_status_id: getAdwordsConversionsData.data[i].lead_status_id, // conversions_data
                conversion_name: getAdwordsConversionsData.data[i].conversion_name, // conversions_data
                conversion_id: getAdwordsConversionsData.data[i].conversion_id, // conversions_data
                feature_name: getAdwordsConversionsData.data[i].feature_name, // conversions_data
                lang,
              }),
            );
          }
        }

        setShowSuccessToast(true);
        // setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      setShowFailedToast(true);
    }
  };

  const handleAdsInputChange = (event) => {
    setAdsId(event.target.value);
  };

  const handleAdsKeywordInputChange = (event) => {
    setAdsKeyword(event.target.value);
  };

  const handleAccountSelector = (event) => {
    setAdsId(event.target.value);
  };

  const handleAccountSelectorChanged = () => {
    // Do nothing
  };

  const successToastCallback = (value) => {
    if (showSuccessToast !== value) {
      setShowSuccessToast(value);
    }

    if (value === false && loading === true) {
      dispatch(googleActions.changeSettingPage("connected"));
    }
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  const validateFailedToastCallback = (value) => {
    setShowValidateFailedToast(value);
  };

  const RenderContent = useMemo(() => {
    try {
      let filteredAccounts = [];
      let initAccountId = 0;

      if (pageLoading === true) {
        return <Loading />;
      } else if (
        accountHierarchyResult.result !== 0 &&
        pageLoading === false &&
        userInfoResult.data.userInfo.isManual !== true
      ) {
        throw new Error("NO_AVAILABLE_ADS_ACCOUNT");
      } else if (
        (accountHierarchyResult.result === 0 || userInfoResult.data.userInfo.isManual === true) &&
        pageLoading === false
      ) {
        if (accountHierarchyResult.data.length === 0 && userInfoResult.data.userInfo.isManual !== true) {
          throw new Error("NO_AVAILABLE_ADS_ACCOUNT");
        }

        // Check manual edit by email
        if (userInfoResult.data.userInfo.isManual === true) {
          return (
            <div>
              <Row alignItems="stretch" className="m-b-10">
                {t("__ads_setup_subtitle")}
              </Row>
              <Row alignItems="stretch">
                <Input
                  type="text"
                  full
                  placeHolder="XXX-XXX-XXXX"
                  onChange={handleAdsInputChange}
                  value={adsId}
                  margin="10px 0px !important"
                />
              </Row>
            </div>
          );
        } else {
          const output = [];

          output.push(
            <Row key="0" alignItems="stretch" className="m-b-10">
              {t("__ads_setup_selection_subtitle")}
            </Row>,
          );
          output.push(
            <StyledInputRow key="1" alignItems="stretch">
              <Input
                type="text-icon"
                full
                placeHolder={t("__ads_setup_filter_placeholder")}
                onChange={(event) => handleAdsKeywordInputChange(event)}
                icon="icon-search"
                value={adsKeyword}
                margin="10px 0px !important"
              />
            </StyledInputRow>,
          );

          if (adsKeyword.length > 0 && adsKeyword.trim() !== "") {
            filteredAccounts = accountHierarchyResult.data.filter(
              (account) =>
                new RegExp(".*" + adsKeyword.replace(/-/g, "") + ".*", "i").test(account.account_id) ||
                new RegExp(".*" + adsKeyword + ".*", "i").test(account.account_name),
            );
          } else {
            filteredAccounts = accountHierarchyResult.data;
          }

          if (filteredAccounts.length > 0 && filteredAccounts[0] && (!adsId || adsId === "")) {
            setAdsId(filteredAccounts[0].account_id);
            initAccountId = filteredAccounts[0].account_id;
          } else {
            initAccountId = 0;
          }

          let displayAccountId = "";
          let displayAccountName = "";

          _forEach(filteredAccounts, function (value, key) {
            if (!value.account_name || value.account_name === "-") {
              displayAccountName = t("__ads_label_google_ads_account");
            } else {
              displayAccountName = value.account_name;
            }

            if (value.account_id) {
              displayAccountId = `${String(value.account_id).slice(0, 3)}-${String(value.account_id).slice(3, 6)}-${String(value.account_id).slice(6)}`;
            } else {
              displayAccountId = value.account_id;
            }

            output.push(
              <AdsAccountSelector key={key + 2}>
                <AdsAccountSelectorRadio>
                  <input
                    type="radio"
                    id={`ads_account_selector_${value.account_id}`}
                    name="ads_account_selector"
                    onClick={handleAccountSelector}
                    onChange={handleAccountSelectorChanged}
                    checked={String(adsId) === String(value.account_id) || initAccountId === value.account_id}
                    value={value.account_id}
                  />
                </AdsAccountSelectorRadio>
                <AdsAccountSelectorDetail htmlFor={`ads_account_selector_${value.account_id}`}>
                  <AdsAccountSelectorName>{displayAccountName}</AdsAccountSelectorName>
                  <AdsAccountSelectorId>{displayAccountId}</AdsAccountSelectorId>
                </AdsAccountSelectorDetail>
              </AdsAccountSelector>,
            );
          });

          return output;
        }
      }
    } catch (error) {
      if (error.message === "NO_AVAILABLE_ADS_ACCOUNT") {
        accountHierarchyResult.result = "";
        dispatch(googleActions.showNoAdsToast(true));
        dispatch(googleActions.changeSettingPage("signin"));
      }
    }
  }, [accountHierarchyResult, adsKeyword, adsId, pageLoading]);

  const view = () => {
    return (
      <StyledContainer>
        <SetupTextHeader>{t("__ads_setup_title")}</SetupTextHeader>

        {RenderContent}

        {pageLoading !== true &&
        (accountHierarchyResult.data.length > 0 || userInfoResult.data.userInfo.isManual === true) ? (
          <StyledBottomWrapper>
            <RowAlignLeft className="m-b-10 m-t-10">
              {t("__ads_setup_or")}&nbsp;
              <SigninButton mode="link" linkTitle={t("__ads_setup_use_other_account")} />
            </RowAlignLeft>

            <RowAlignRight alignItems="stretch">
              <Button type="loading" onClick={handleSaveButton} loading={loading}>
                {t("__ads_setup_save")}
              </Button>
            </RowAlignRight>
          </StyledBottomWrapper>
        ) : (
          ""
        )}

        <Toast
          show={showSuccessToast}
          message={t("__ads_setup_save_success")}
          bgColor="#4CAF50"
          callback={successToastCallback}
        ></Toast>
        <Toast
          show={showFailedToast}
          message={t("__ads_setup_save_failed")}
          bgColor="#F44336"
          callback={failedToastCallback}
        ></Toast>
        <Toast
          show={showValidateFailedToast}
          message={t("__ads_setup_validate_params_failed")}
          bgColor="#F44336"
          callback={validateFailedToastCallback}
        ></Toast>
      </StyledContainer>
    );
  };

  return view();
};

export default Setup;

const StyledInputRow = styled(Row)`
  position: sticky;
  top: -16px;
  background-color: #fff;
  padding-bottom: 10px;
`;

const StyledBottomWrapper = styled.div`
  position: sticky;
  bottom: -16px;
  padding: 10px 0px 10px;
  background-color: #fff;
`;

const StyledContainer = styled.div`
  font-size: 14px;
  color: #555;

  .m-b-10 {
    margin-bottom: 10px;
  }

  .m-t-10 {
    margin-top: 10px;
  }
`;

const RowAlignRight = styled(Row)`
  justify-content: flex-end;
`;

const RowAlignLeft = styled(Row)`
  justify-content: flex-start;
`;

const SetupTextHeader = styled(TextHeader)`
  font-size: 18px;
  margin-bottom: 10px;
`;

const AdsAccountSelector = styled.div`
  display: flex;
  align-items: center;
  border-bottom: solid 1px #eee;
  cursor: pointer;

  &:hover {
    background-color: #e3f2fd;
  }
`;

const AdsAccountSelectorRadio = styled.div`
  flex-shrink: 1;

  input {
    cursor: pointer;
  }
`;

const AdsAccountSelectorDetail = styled.label`
  flex-grow: 1;
  padding: 10px 10px 10px 5px;
  cursor: pointer;
`;

const AdsAccountSelectorName = styled.div``;

const AdsAccountSelectorId = styled.div`
  font-size: 12px;
  color: #9e9e9e;
`;
